import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Modal,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DeleteOutlined as DeleteIcon,
  DownloadOutlined,
  DownloadOutlined as ExportIcon,
} from "@mui/icons-material";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useSnackbar } from "notistack";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { applySortingWithAccessorKeys } from "./utils/tableUtils.js";
import ToolbarDatePeriod from "../ToolbarDatePeriod.jsx";
import dayjs from "dayjs";
import { getDatum, getEuro } from "@accredo/fe-shared/services/formatService.js";
import ClaimStatusBadge from "../ClaimStatusBadge.jsx";
import { colorsFoundation, typographyTokens } from "@accredo/fe-shared/theme/index.js";
import PropTypes from "prop-types";
import Button from "@accredo/fe-shared/theme/components/Button.jsx";

export default function ControllingClaimsTable({ customerId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [period, setPeriod] = useState({
    // default to previous month
    period: "month",
    startDate: dayjs().subtract(1, "month").startOf("month"),
    endDate: dayjs().subtract(1, "month").endOf("month"),
    title: "",
  });
  const [exportCosts, setExportCosts] = useState(true);
  const [isExportDialogOpen, setExportDialogOpen] = useState(false);

  const tableKey = "controlling_claims";
  const storageKey = "v2_table_controlling_claims";
  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Controlling - Schäden",
        route: "/data/controlling",
        exportRoute: `/data/controlling/export?include_costs=${exportCosts}`,
        exportFilename:
          "controlling_schaeden_zeitraum_" +
          period.startDate.format("YYYY-MM-DD") +
          "_" +
          period.endDate.format("YYYY-MM-DD"),
        pageSize: 10,
        storageKey: storageKey,
        extraParams: {
          filters: {
            kunde_id: customerId,
            period: {
              from: period.startDate.format("YYYY-MM-DD"),
              to: period.endDate.format("YYYY-MM-DD"),
            },
          },
        },
      },
    ],
    [period.startDate, period.endDate, exportCosts]
  );

  const { tableStates, updateTableState, exportData, tableExportStates } =
    useMultiTableData(tablesConfig, enqueueSnackbar);
  const isExporting = tableExportStates[tableKey].isExporting;
  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];
  const totalRows = tableState.totalRows ?? rows.length;
  const pageIndex = tableState.pageIndex ?? 0;
  const pageSize = tableState.pageSize ?? 10;
  const isLoading = tableState.isLoading ?? false;
  const hasLoaded = tableState.hasLoaded ?? false;
  const objectRefNumLabel = rows[0]?.object_ref_num_label ?? null;

  useEffect(() => {
    updateTableState(tableKey, {
      pageIndex: 0,
      shouldFetch: true,
    });
  }, [
    period.startDate?.format("YYYY-MM-DD"),
    period.endDate?.format("YYYY-MM-DD"),
  ]);

  // Spalten-Definition
  const columns = useMemo(
    () => [
      {
        accessorKey: "schaden_nummer_accredo",
        header: "Schadennummer",
        size: 200,
        Cell: ({ row, cell }) => (
          <>
            <Tooltip title={"Schadennummer Accredo"}>
              <Typography
                variant="body2"
                sx={{ fontWeight: typographyTokens.weight.semibold }}
              >
                {cell.getValue()}
              </Typography>
            </Tooltip>
            {row.original?.schaden_nummer_vu && (
              <Tooltip title={"Schadennummer Versicherer"}>
                <Typography
                  variant="body2"
                  sx={{
                    color: colorsFoundation.neutral[500],
                    fontSize: typographyTokens.size.sm,
                  }}
                >
                  VR: {row.original.schaden_nummer_vu}
                </Typography>
              </Tooltip>
            )}
            {row.original?.schaden_nummer_kd && (
              <Tooltip title={"Interne Schadennummer"}>
                <Typography
                  variant="body2"
                  sx={{
                    color: colorsFoundation.neutral[500],
                    fontSize: typographyTokens.size.sm,
                  }}
                >
                  KD: {row.original.schaden_nummer_kd}
                </Typography>
              </Tooltip>
            )}
          </>
        ),
      },
      {
        accessorKey: "schaden_datum",
        header: "Datum",
        muiTableHeadCellProps: {
          title: "Schadendatum",
        },
        size: 180,
        Cell: ({ cell }) => <>{getDatum(cell.getValue())}</>,
      },
      {
        accessorKey: "schadenmeldung_datum",
        header: "Gemeldet am",
        muiTableHeadCellProps: {
          title: "Datum der Schadenmeldung",
        },
        size: 180,
        Cell: ({ cell }) => <>{getDatum(cell.getValue())}</>,
      },
      {
        accessorKey: "schaden_hoehe",
        header: "Eingereichter Betrag",
        size: 180,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ textAlign: "right" }}>
            {getEuro(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "auszahlungsbetrag",
        header: "Auszahlungsbetrag",
        muiTableHeadCellProps: {
          title: "Auszahlungsbetrag",
        },
        size: 180,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ textAlign: "right" }}>
            {getEuro(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "vertragsnummer",
        header: "Vertragsnr.",
        muiTableHeadCellProps: {
          title: "Vertragsnummer",
        },
        Cell: ({ row }) => (
          <>{`${row.original?.vertragsnummer} (${row.original?.insurer_name})`}</>
        ),
      },
      {
        accessorKey: "sparte",
        header: "Sparte",
        muiTableHeadCellProps: {
          title: "Sparte",
        },
      },
      ...(objectRefNumLabel
        ? [
            {
              accessorKey: "wirtschaftseinheit",
              header: objectRefNumLabel,
              muiTableHeadCellProps: {
                title: objectRefNumLabel,
              },
              Cell: ({ row }) => (
                <>{row.original?.objekt?.customer_reference_number}</>
              ),
            },
          ]
        : []),
      {
        accessorKey: "objekt",
        header: "Objekt/Adresse",
        muiTableHeadCellProps: {
          title: "betroffenes Objekt/Adresse ",
        },
        Cell: ({ row }) => {
          const object = row.original?.objekt;
          // display object address only if it differs from the object description
          const obj_address =
            object.address && object.address !== object.bezeichnung
              ? object.address
              : null;
          // display claim address only if it differs from the object addresss
          const claim_address =
            row.original?.schaden_address &&
            row.original?.schaden_address !== obj_address &&
            row.original?.schaden_address !== object.bezeichnung
              ? row.original?.schaden_address
              : null;

          return (
            <>
              <Tooltip title="Objekt">
                <Typography variant="body2">{object?.bezeichnung}</Typography>
              </Tooltip>
              {claim_address && (
                <Tooltip title="Adresse des Schadens">
                  <Typography
                    variant="body2"
                    sx={{ fontSize: typographyTokens.size.sm }}
                  >
                    {claim_address}
                  </Typography>
                </Tooltip>
              )}
              {obj_address && (
                <Tooltip title="Adresse des Objekts">
                  <Typography variant="body2">{obj_address}</Typography>
                </Tooltip>
              )}
            </>
          );
        },
      },
      {
        accessorKey: "sb_betrag",
        header: "SB",
        muiTableHeadCellProps: {
          title: "Abzüge/Selbsthehalte",
        },
        size: 120,
        Cell: ({ cell, row }) => {
          const amount = parseFloat(cell.getValue());
          return (
            <Tooltip title={row.original.sb_label}>
              <span>{getEuro(amount)}</span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ cell }) => {
          const status = cell.getValue();
          return (
            <ClaimStatusBadge
              status={status?.value || status}
              label={status?.label || status}
              size="small"
            />
          );
        },
      },
    ],
    [objectRefNumLabel]
  );

  const handleExportExcel = () => {
    setExportDialogOpen(true);
  };

  const toolbarActions = [
    {
      label: "Excel Export",
      type: "icon",
      variant: "tertiary",
      icon: tableExportStates[tableKey]?.isExporting ? (
        <CircularProgress color="primary" size={32} thickness={5} />
      ) : (
        <DownloadOutlined />
      ),
      onClick: handleExportExcel,
      disabled: tableExportStates[tableKey]?.isExporting,
    },
  ];

  // Pagination-Handler
  const handlePaginationChange = (pagination) => {
    const next =
      typeof pagination === "function"
        ? pagination({ pageIndex, pageSize })
        : pagination;

    if (!next) return;

    updateTableState(tableKey, {
      pageIndex: next.pageIndex,
      pageSize: next.pageSize,
    });
  };

  const flattenCostRows = (costs) => {
    if (!Array.isArray(costs) || costs.length === 0) return [];

    const rows = [];

    costs.forEach((cost, cost_index) => {
      if (!cost) {
        return;
      }

      const cost_id = cost.id ?? `idx-${cost_index}`;
      const bookings = Array.isArray(cost.bookings) ? cost.bookings : [];
      const has_bookings = bookings.length > 0;
      rows.push({
        id: `cost-${cost_id}`,
        is_cost_row: true,
        is_group_end: !has_bookings,
        cost_label: cost.cost_label,
        invoice_number: cost.invoice_number,
        company_name: cost.company_name,
        amount: cost.amount,
        paid_at: null,
      });

      bookings.forEach((booking, booking_index) => {
        if (!booking) return;
        const booking_id = booking.id ?? `idx-${booking_index}`;
        rows.push({
          id: `booking-${cost_id}-${booking_id}`,
          is_cost_row: false,
          is_group_end: booking_index === bookings.length - 1,
          cost_label: "",
          invoice_number: "",
          company_name: "",
          amount: booking.amount,
          paid_at: booking.paid_at,
        });
      });
    });

    return rows;
  };

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        hasLoaded={hasLoaded}
        title={`Schäden ${period?.title}`}
        enableRowExpansion
        renderRowExpansionContent={(row) => row._expand}
        enablePagination
        manualPagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalRows={totalRows}
        headerStartSlot={
          <ToolbarDatePeriod
            value={period}
            onChange={({ period, startDate, endDate, title }) => {
              setPeriod({ period, startDate, endDate, title });
            }}
            showTitle={false}
          />
        }
        toolbarActions={toolbarActions}
        onChangePage={handlePaginationChange}
        onChangeSorting={(sorting) =>
          updateTableState(tableKey, {
            sorting: applySortingWithAccessorKeys(sorting, columns),
          })
        }
        onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
        onChangeGlobalFilter={(searchword) =>
          updateTableState(tableKey, { searchword })
        }
        storageKey={storageKey}
        muiDetailPanelProps={{
          sx: {
            padding: 0,
            backgrundColor: colorsFoundation.neutral[50],
          },
        }}
        renderDetailPanel={({ row }) => {
          if (!row.original.costs) {
            return null;
          }

          return (
            <>
              <Box sx={{ p: 0 }}>
                {Array.isArray(row.original.costs) &&
                row.original.costs.length > 0 ? (
                  <DataTable
                    variant="bare"
                    columns={[
                      {
                        accessorKey: "cost_label",
                        header: "Kostenposition",
                      },
                      {
                        accessorKey: "invoice_number",
                        header: "Rechnungsnummer",
                      },
                      {
                        accessorKey: "company_name",
                        header: "Firma",
                      },
                      {
                        accessorKey: "amount",
                        header: "Betrag",
                        size: 150,
                        Cell: ({ cell }) => (
                          <Typography sx={{ textAlign: "right" }}>
                            {getEuro(cell.getValue())}
                          </Typography>
                        ),
                      },
                      {
                        accessorKey: "paid_at",
                        header: "Bezahlt am",
                        size: 140,
                        Cell: ({ cell }) => <>{getDatum(cell.getValue())}</>,
                      },
                    ]}
                    data={flattenCostRows(row.original.costs)}
                    muiTableBodyRowProps={({ row }) => {
                      const is_group_end = Boolean(row.original?.is_group_end);
                      return {
                        sx: {
                          "& td": {
                            borderBottom: is_group_end
                              ? `1px solid ${colorsFoundation.neutral[500]} !important`
                              : `1px solid ${colorsFoundation.neutral[200]} !important`,
                          },
                        },
                      };
                    }}
                    pageSize={99999}
                    enableFooter={false}
                    enableTopToolbar={false}
                    enablePagination={false}
                    enableColumnFilters={false}
                    enableSorting={true}
                    enableBottomToolbar={false}
                    enableGlobalFilter={true}
                    initialState={{
                      globalFilter: tableState.searchword,
                    }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ pl: 2 }}>
                    Keine Kostenpositionen
                  </Typography>
                )}
              </Box>
            </>
          );
        }}
      />
      <Modal
        open={isExportDialogOpen}
        onClose={() => {
          setIsExportDialogOpen(false);
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 420,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Daten exportieren
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Daten als Excel-Liste exportieren
          </Typography>
          <Box>
            <FormControlLabel
              sx={{ mb: 4 }}
              control={
                <Switch
                  sx={{ mr: 2, ml: "10px" }}
                  checked={exportCosts}
                  onChange={(ev) => {
                    setExportCosts(ev.target.checked);
                  }}
                />
              }
              label="Kostenpositionen exportieren"
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={() => {
                setExportDialogOpen(false);
              }}
              variant="tertiary"
              disabled={isExporting}
            >
              Abbrechen
            </Button>
            <Button
              onClick={async () => {
                await exportData(tableKey);
                setExportDialogOpen(false);
              }}
              variant="primary"
              disabled={isExporting}
              startIcon={<ExportIcon />}
            >
              {isExporting ? "Wird exportiert..." : "Exportieren"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

ControllingClaimsTable.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
